import { createBrowserRouter, Navigate, redirect } from 'react-router-dom'

import type { RouteObject } from 'react-router-dom'

import { AppShell } from '@/components/layout/app-shell'
import { RouteErrorBoundary } from '@/components/shared/route-error-boundary'
import { AccountShell } from '@/features/customer-portal/components/account-shell'
import { TeamShell } from '@/features/team/components/team-shell'
import { api } from '@/lib/api-client'
import { AuthLayout } from '@/routes/_auth/auth-layout'

export async function privateLoader() {
  try {
    const { data } = await api.get<{ user?: { accountType?: string } } | null>(
      '/api/auth/get-session',
    )
    if (!data?.user) {
      const next = encodeURIComponent(window.location.pathname + window.location.search)
      // React Router loaders sinalizam redirect via `throw new Response(...)` — pattern oficial.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect(`/sign-in?next=${next}`)
    }
    // Fronteira real do painel: só staff entra em /app. Cliente (avulso ou admin de time) é
    // reencaminhado para o portal do cliente; o accountLoader/teamLoader afina a partir daí.
    if (data.user.accountType !== 'staff') {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect('/conta')
    }
    return data.user
  } catch (err) {
    if (err instanceof Response) throw err
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect('/sign-in')
  }
}

export async function requirePermission(permission: string) {
  try {
    const { data } = await api.get<{ user?: { id: string }; permissions?: string[] } | null>(
      '/api/auth/get-session',
    )
    if (!data?.user) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect('/sign-in')
    }
    if (!data.permissions?.includes(permission)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect('/app')
    }
    return null
  } catch (err) {
    if (err instanceof Response) throw err
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect('/sign-in')
  }
}

export async function teamLoader() {
  try {
    const { data } = await api.get<{ user?: { accountType?: string } } | null>(
      '/api/auth/get-session',
    )
    if (!data?.user) {
      const next = encodeURIComponent(window.location.pathname + window.location.search)
      // React Router loaders sinalizam redirect via `throw new Response(...)` — pattern oficial.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect(`/team/sign-in?next=${next}`)
    }
    if (data.user.accountType === 'staff') {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect('/app')
    }
    return data.user
  } catch (err) {
    if (err instanceof Response) throw err
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect('/team/sign-in')
  }
}

export async function accountLoader() {
  try {
    const { data } = await api.get<{ user?: { accountType?: string } } | null>(
      '/api/auth/get-session',
    )
    if (!data?.user) {
      const next = encodeURIComponent(window.location.pathname + window.location.search)
      // React Router loaders sinalizam redirect via `throw new Response(...)` — pattern oficial.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect(`/conta/entrar?next=${next}`)
    }
    if (data.user.accountType === 'staff') {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect('/app')
    }
    return data.user
  } catch (err) {
    if (err instanceof Response) throw err
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect('/conta/entrar')
  }
}

// Retorno do checkout: sessão viva vai direto para a assinatura, onde o status aparece. Sem
// sessão (pagou noutro dispositivo, ou o cookie expirou) cai na página pública de recebimento.
// Falha de rede não pode virar erro de tela aqui: no pior caso mostra a página pública.
export async function paymentReturnLoader() {
  try {
    const { data } = await api.get<{ user?: { accountType?: string } } | null>(
      '/api/auth/get-session',
    )
    if (data?.user && data.user.accountType !== 'staff') {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect('/conta#assinatura')
    }
  } catch (err) {
    if (err instanceof Response) throw err
  }
  return null
}

const routes: RouteObject[] = [
  {
    path: '/',
    lazy: () =>
      import('@/features/marketing/pages/landing-page').then((m) => ({
        Component: m.LandingPage,
      })),
  },

  {
    element: <AuthLayout />,
    children: [
      {
        path: '/sign-up',
        lazy: () =>
          import('@/features/signup/pages/sign-up-page').then((m) => ({ Component: m.SignUpPage })),
      },
      {
        path: '/sign-in',
        lazy: () =>
          import('@/features/auth/pages/sign-in-page').then((m) => ({ Component: m.SignInPage })),
      },
      // Login unificado: os antigos sign-ins de time/cliente viram redirect para não quebrar
      // bookmarks/links (o loader de cada área ainda aponta para cá quando não há sessão).
      { path: '/team/sign-in', element: <Navigate to="/sign-in" replace /> },
      { path: '/conta/entrar', element: <Navigate to="/sign-in" replace /> },
      {
        path: '/forgot-password',
        lazy: () =>
          import('@/features/auth/pages/forgot-password-page').then((m) => ({
            Component: m.ForgotPasswordPage,
          })),
      },
      {
        path: '/verify-email',
        lazy: () =>
          import('@/features/auth/pages/verify-email-page').then((m) => ({
            Component: m.VerifyEmailPage,
          })),
      },
      {
        path: '/reset-password',
        lazy: () =>
          import('@/features/auth/pages/reset-password-page').then((m) => ({
            Component: m.ResetPasswordPage,
          })),
      },
    ],
  },

  {
    path: '/app',
    element: <AppShell />,
    loader: privateLoader,
    children: [
      // Sem `requirePermission` de propósito: este é o destino do redirect de permissão
      // faltante, e guardá-lo criaria loop infinito. A página trata a ausência da permissão.
      {
        index: true,
        lazy: () =>
          import('@/features/dashboard/pages/dashboard-page').then((m) => ({
            Component: m.DashboardPage,
          })),
      },
      {
        path: 'admin/roles',
        lazy: () =>
          import('@/features/admin/pages/roles-page').then((m) => ({ Component: m.RolesPage })),
        loader: () => requirePermission('admin:roles'),
      },
      {
        path: 'admin/users',
        lazy: () =>
          import('@/features/admin/pages/users-page').then((m) => ({ Component: m.UsersPage })),
        loader: () => requirePermission('admin:users'),
      },
      {
        path: 'customers',
        lazy: () =>
          import('@/features/customers/pages/customers-page').then((m) => ({
            Component: m.CustomersPage,
          })),
        loader: () => requirePermission('billing:customers'),
      },
      {
        path: 'companies',
        lazy: () =>
          import('@/features/companies/pages/companies-page').then((m) => ({
            Component: m.CompaniesPage,
          })),
        loader: () => requirePermission('billing:companies'),
      },
      {
        path: 'sellers',
        lazy: () =>
          import('@/features/sellers/pages/sellers-page').then((m) => ({
            Component: m.SellersPage,
          })),
        loader: () => requirePermission('billing:sellers'),
      },
      {
        path: 'plans',
        lazy: () =>
          import('@/features/plans/pages/plans-page').then((m) => ({
            Component: m.PlansPage,
          })),
        loader: () => requirePermission('billing:customers'),
      },
      {
        path: 'companies/:companyId',
        lazy: () =>
          import('@/features/companies/pages/company-detail-page').then((m) => ({
            Component: m.CompanyDetailPage,
          })),
        loader: () => requirePermission('billing:companies'),
      },
      {
        path: 'commissions',
        lazy: () =>
          import('@/features/commissions/pages/commissions-page').then((m) => ({
            Component: m.CommissionsPage,
          })),
        loader: () => requirePermission('billing:commissions'),
      },
      {
        path: 'commissions/:sellerId',
        lazy: () =>
          import('@/features/commissions/pages/seller-commissions-page').then((m) => ({
            Component: m.SellerCommissionsPage,
          })),
        loader: () => requirePermission('billing:commissions'),
      },
      {
        path: 'invoices',
        lazy: () =>
          import('@/features/invoices/pages/invoices-page').then((m) => ({
            Component: m.InvoicesPage,
          })),
        loader: () => requirePermission('billing:invoices'),
      },
      {
        path: 'invoices/:invoiceId',
        lazy: () =>
          import('@/features/invoices/pages/invoice-detail-page').then((m) => ({
            Component: m.InvoiceDetailPage,
          })),
        loader: () => requirePermission('billing:invoices'),
      },
      {
        path: 'suggestions',
        lazy: () =>
          import('@/features/suggestions/pages/suggestions-page').then((m) => ({
            Component: m.SuggestionsPage,
          })),
        loader: () => requirePermission('support:suggestions'),
      },
      // Sem `requirePermission`: o próprio perfil é de todo usuário logado.
      {
        path: 'perfil',
        lazy: () =>
          import('@/features/profile/pages/profile-page').then((m) => ({
            Component: m.ProfilePage,
          })),
      },
    ],
  },

  {
    path: '/team',
    element: <TeamShell />,
    loader: teamLoader,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/team/pages/team-overview-page').then((m) => ({
            Component: m.TeamOverviewPage,
          })),
      },
      {
        path: 'members',
        lazy: () =>
          import('@/features/team/pages/team-members-page').then((m) => ({
            Component: m.TeamMembersPage,
          })),
      },
      {
        path: 'invoices',
        lazy: () =>
          import('@/features/team/pages/team-invoices-page').then((m) => ({
            Component: m.TeamInvoicesPage,
          })),
      },
      {
        path: 'perfil',
        lazy: () =>
          import('@/features/profile/pages/profile-page').then((m) => ({
            Component: m.ProfilePage,
          })),
      },
    ],
  },

  {
    path: '/conta',
    element: <AccountShell />,
    loader: accountLoader,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/customer-portal/pages/account-overview-page').then((m) => ({
            Component: m.AccountOverviewPage,
          })),
      },
      {
        path: 'faturas',
        lazy: () =>
          import('@/features/customer-portal/pages/account-invoices-page').then((m) => ({
            Component: m.AccountInvoicesPage,
          })),
      },
      // A assinatura passou a viver na visão geral (era o mesmo assunto em três lugares). O
      // redirect mantém de pé os links já enviados por e-mail e os bookmarks.
      { path: 'assinatura', element: <Navigate to="/conta#assinatura" replace /> },
      {
        path: 'perfil',
        lazy: () =>
          import('@/features/profile/pages/profile-page').then((m) => ({
            Component: m.ProfilePage,
          })),
      },
    ],
  },

  {
    path: '/pagamento/retorno',
    // Quem volta do checkout quase sempre ainda está logado — o cookie de sessão viaja na
    // navegação de volta. Mandar essa pessoa para a tela de login é pedir que ela faça de novo
    // algo que já está feito. Só quem realmente não tem sessão vê a página com o botão de entrar.
    loader: paymentReturnLoader,
    lazy: () =>
      import('@/features/customer-portal/pages/payment-return-page').then((m) => ({
        Component: m.PaymentReturnPage,
      })),
  },

  { path: '*', element: <Navigate to="/" replace /> },
]

// `errorElement` em toda rota de topo: sem ele o React Router mostra a tela crua de erro dele
// (foi o que o cliente viu ao cair num chunk que sumiu no deploy). Rota filha sem boundary
// próprio sobe para o ancestral mais próximo.
export const router = createBrowserRouter(
  routes.map((route) => ({ errorElement: <RouteErrorBoundary />, ...route })),
)

